import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../Enterprise.css';
import { ContactForm, ApiPlayground, Testimonials, FreeTrialForm, AnimatedCounter } from '../components/common';

const EnterprisePage = () => {
  // Apply dark theme to body when on enterprise page
  useEffect(() => {
    document.body.style.background = '#0a0a0a';
    document.body.style.color = '#fff';
    return () => {
      document.body.style.background = '';
      document.body.style.color = '';
    };
  }, []);

  return (
    <div className="enterprise-page">
      {/* Pilot Banner */}
      <div className="pilot-banner">
        <div className="pilot-banner-inner">
          <span className="pilot-badge">Early Access</span>
          <span className="pilot-text"><strong>Pilot Program:</strong> 3 months full access for $2,500. Limited to 20 partners.</span>
          <a href="#contact" className="pilot-cta">Apply Now</a>
        </div>
      </div>

      {/* Enterprise Header */}
      <header className="enterprise-header">
        <div className="enterprise-header-inner">
          <Link to="/" className="enterprise-logo">Framecast <span>Enterprise</span></Link>
          <nav className="enterprise-nav">
            <a href="#pricing">Pricing</a>
            <a href="#products">Solutions</a>
            <a href="#api">API</a>
            <a href="#contact" className="enterprise-nav-cta">Contact Sales</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="enterprise-hero">
        <div className="hero-label">Narrative Intelligence Platform</div>
        <h1>See how the world frames the news before it moves markets</h1>
        <p className="hero-subtitle">
          Real-time framing analysis across 5 regions. Quantified narrative divergence. 
          Built for investors, comms teams, and researchers.
        </p>
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-value"><AnimatedCounter value={33} duration={1500} /></div>
            <div className="hero-stat-label">Global Sources</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-value">5</div>
            <div className="hero-stat-label">Regions Tracked</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-value">4hr</div>
            <div className="hero-stat-label">Refresh Cycle</div>
          </div>
        </div>
        <div className="hero-ctas">
          <a href="#pricing" className="hero-cta-primary">View Pricing</a>
          <a href="#api" className="hero-cta-secondary">Try API Free</a>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="value-prop">
        <div className="value-prop-inner">
          <div className="section-label">The Problem</div>
          <h2 className="section-title">Same story. Different framing. The gap is the signal.</h2>
          
          <div className="value-grid">
            <div className="value-item">
              <div className="value-icon">📡</div>
              <h3>Information Asymmetry</h3>
              <p>When US and Chinese media frame the same trade story differently, tensions are building. Traditional monitoring misses cross-regional narrative gaps.</p>
            </div>
            <div className="value-item">
              <div className="value-icon">🌐</div>
              <h3>Regional Blind Spots</h3>
              <p>Your team reads US and UK sources. But how is the same story framed in India, Middle East, or East Asia? That's where the signal hides.</p>
            </div>
            <div className="value-item">
              <div className="value-icon">📊</div>
              <h3>Unquantified Framing</h3>
              <p>You know framing matters. But you can't measure it. We quantify narrative divergence so you can track it over time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FDI Explainer */}
      <section className="fdi-section">
        <div className="fdi-inner">
          <div className="fdi-content">
            <h2>The Framing Divergence Index</h2>
            <p>
              Our FDI score quantifies narrative gaps across regions on a 0-100 scale. 
              Low scores mean global consensus. High scores signal divergent framing.
            </p>
            <p>
              Track how FDI evolves on stories you care about. When regional narratives 
              start pulling apart, you'll see it in the score before it shows up in headlines.
            </p>
            <p>
              The FDI isn't sentiment. It's not bias. It's the measurable distance between 
              how different regions frame the same facts.
            </p>
          </div>
          <div className="fdi-score-card">
            <div className="fdi-score-label">Sample FDI Score</div>
            <div className="fdi-score-value">79.6</div>
            <div className="fdi-score-story">"US Semiconductor Export Controls"</div>
            <div className="fdi-score-insight">High divergence: US frames as "security" / China frames as "containment"</div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="enterprise-pricing" id="pricing">
        <div className="pricing-inner">
          <div className="pricing-header">
            <div className="section-label">Pricing</div>
            <h2>Simple, transparent pricing</h2>
            <p>Start with the free API tier. Scale when you need more.</p>
          </div>
          
          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-name">Starter</div>
              <div className="pricing-price">$99<span>/mo</span></div>
              <p className="pricing-desc">For individuals and small teams exploring narrative data</p>
              <ul className="pricing-features">
                <li>10,000 API calls/month</li>
                <li>All 5 regions</li>
                <li>Framing tags + FDI scores</li>
                <li>7-day data retention</li>
                <li>Email support</li>
              </ul>
              <a href="#contact" className="pricing-cta">Start Free Trial</a>
            </div>
            
            <div className="pricing-card featured">
              <div className="pricing-badge">Most Popular</div>
              <div className="pricing-name">Professional</div>
              <div className="pricing-price">$299<span>/mo</span></div>
              <p className="pricing-desc">For teams that need deeper analysis and historical data</p>
              <ul className="pricing-features">
                <li>50,000 API calls/month</li>
                <li>Story clustering + comparison</li>
                <li>90-day historical archive</li>
                <li>Daily digest reports</li>
                <li>Webhook alerts</li>
                <li>Priority support</li>
              </ul>
              <a href="#contact" className="pricing-cta">Start Free Trial</a>
            </div>
            
            <div className="pricing-card">
              <div className="pricing-name">Team</div>
              <div className="pricing-price">$499<span>/mo</span></div>
              <p className="pricing-desc">For organizations tracking multiple topics or companies</p>
              <ul className="pricing-features">
                <li>Unlimited API calls</li>
                <li>Full historical archive</li>
                <li>Custom topic tracking</li>
                <li>5 team seats</li>
                <li>Slack integration</li>
                <li>Dedicated support</li>
              </ul>
              <a href="#contact" className="pricing-cta">Contact Sales</a>
            </div>
          </div>
          
          <p className="pricing-note">
            <strong>Annual discount:</strong> Pay yearly, get 2 months free. 
            <strong>Pilot program:</strong> $2,500 for 3 months full access.
          </p>
        </div>
      </section>

      {/* Products/Solutions */}
      <section className="products-section" id="products">
        <div className="products-inner">
          <div className="products-header">
            <div className="section-label">Solutions</div>
            <h2>Built for how you work</h2>
          </div>
          
          <div className="products-grid">
            <div className="product-card">
              <div className="product-icon">📈</div>
              <h4 className="product-name">Investment Research</h4>
              <p className="product-desc">Track narrative divergence on sectors, companies, and geopolitical topics. Get alerts when framing gaps widen on stories that matter to your portfolio.</p>
              <p className="product-audience"><strong>For:</strong> Analysts, PMs, research teams</p>
              <p className="product-price">From $299/month</p>
            </div>
            
            <div className="product-card">
              <div className="product-icon">📢</div>
              <h4 className="product-name">Corporate Communications</h4>
              <p className="product-desc">Monitor how your company is framed across global markets. Detect regional reputation gaps before they become crises.</p>
              <p className="product-audience"><strong>For:</strong> Comms teams, IR, PR agencies</p>
              <p className="product-price">From $499/month</p>
            </div>
            
            <div className="product-card">
              <div className="product-icon">🏛️</div>
              <h4 className="product-name">Policy Research</h4>
              <p className="product-desc">Track how policy topics are framed across jurisdictions. Understand the narrative landscape before engaging stakeholders.</p>
              <p className="product-audience"><strong>For:</strong> Think tanks, gov affairs, consultancies</p>
              <p className="product-price">From $299/month</p>
            </div>
            
            <div className="product-card">
              <div className="product-icon">📰</div>
              <h4 className="product-name">Journalism &amp; Research</h4>
              <p className="product-desc">Find how other regions covered your story. Discover sources and angles invisible from your home market.</p>
              <p className="product-audience"><strong>For:</strong> Journalists, academics, researchers</p>
              <p className="product-price">From $29/month</p>
            </div>
          </div>
        </div>
      </section>

      {/* API Section */}
      <section className="api-section" id="api">
        <div className="api-inner">
          <div className="api-content">
            <div className="section-label">For Developers</div>
            <h2>Framing Detection API</h2>
            <p>
              Integrate narrative analysis into your own products. Send headlines, 
              get back framing tags, regional comparisons, and FDI scores.
            </p>
            
            <div className="api-tiers">
              <div className="api-tier">
                <span className="api-tier-name">Free Tier</span>
                <span className="api-tier-price">1,000 calls/month</span>
              </div>
              <div className="api-tier">
                <span className="api-tier-name">Starter</span>
                <span className="api-tier-price">$99/month (10K calls)</span>
              </div>
              <div className="api-tier">
                <span className="api-tier-name">Professional</span>
                <span className="api-tier-price">$299/month (50K calls)</span>
              </div>
              <div className="api-tier">
                <span className="api-tier-name">Volume</span>
                <span className="api-tier-price">$0.002 per call</span>
              </div>
            </div>
            
            <a href="#contact" className="api-cta">Get Free API Key</a>
          </div>
          <div className="api-code">
            <pre>{`# Analyze a headline
curl -X POST https://api.framecast.news/v1/analyze \\
  -H "Authorization: Bearer $API_KEY" \\
  -d '{
    "headline": "US Expands Chip Export Controls",
    "region": "us"
  }'

# Response
{
  "tags": ["policy", "security_frame"],
  "fdi_score": 79.6,
  "divergent_regions": ["east_asia"]
}`}</pre>
          </div>
        </div>
      </section>

      {/* API Playground */}
      <section className="playground-section">
        <div className="playground-inner">
          <div className="section-label">Try It Now</div>
          <h2>Interactive API Playground</h2>
          <p>Test our framing analysis API with any headline. No API key required for demo.</p>
          <ApiPlayground />
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Use Cases */}
      <section className="usecases-section" id="usecases">
        <div className="usecases-inner">
          <div className="usecases-header">
            <div className="section-label">Use Cases</div>
            <h2>How teams use narrative intelligence</h2>
          </div>
          
          <div className="usecases-grid">
            <div className="usecase-card">
              <div className="usecase-industry">Investment</div>
              <h3>Pre-Trade Narrative Check</h3>
              <p>Before entering a position, check how the thesis is being framed across regions. High FDI scores on related topics signal divergent narratives worth investigating.</p>
            </div>
            
            <div className="usecase-card">
              <div className="usecase-industry">Communications</div>
              <h3>Regional Reputation Monitoring</h3>
              <p>Track how your company or client is framed across global markets. Catch regional sentiment gaps before they escalate into reputational issues.</p>
            </div>
            
            <div className="usecase-card">
              <div className="usecase-industry">Policy</div>
              <h3>Regulatory Landscape Mapping</h3>
              <p>Track how proposed regulations are framed across jurisdictions. Understand where narrative alignment exists and where positions are hardening.</p>
            </div>
            
            <div className="usecase-card">
              <div className="usecase-industry">Research</div>
              <h3>Cross-Border Story Development</h3>
              <p>Find how other regions covered a story you're investigating. Discover sources, angles, and context invisible from your home market.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Section */}
      <section className="data-section">
        <div className="data-inner">
          <h2>The Data Behind the Intelligence</h2>
          
          <div className="data-grid">
            <div className="data-stat">
              <div className="data-stat-value">33</div>
              <div className="data-stat-label">Sources Tracked</div>
            </div>
            <div className="data-stat">
              <div className="data-stat-value">5</div>
              <div className="data-stat-label">Global Regions</div>
            </div>
            <div className="data-stat">
              <div className="data-stat-value">4hr</div>
              <div className="data-stat-label">Refresh Cycle</div>
            </div>
            <div className="data-stat">
              <div className="data-stat-value">90d</div>
              <div className="data-stat-label">Archive Depth</div>
            </div>
          </div>
          
          <div className="data-regions">
            <span className="data-region">United States</span>
            <span className="data-region">Europe</span>
            <span className="data-region">India</span>
            <span className="data-region">Middle East</span>
            <span className="data-region">East Asia</span>
          </div>
        </div>
      </section>

      {/* CTA Section with Contact Form */}
      <section className="enterprise-cta" id="contact">
        <div className="cta-content">
          <div className="cta-text">
            <h2>Ready to see narrative intelligence in action?</h2>
            <p>Fill out the form and we'll be in touch within 24 hours to discuss your use case.</p>
            <div className="cta-trial">
              <FreeTrialForm />
            </div>
          </div>
          <div className="cta-form">
            <h3>Contact Sales</h3>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="enterprise-footer">
        <div className="enterprise-footer-inner">
          <div className="footer-brand">
            <div className="footer-logo">Framecast Enterprise</div>
            <p>Narrative intelligence for global decision-makers</p>
          </div>
          <div className="footer-links">
            <div className="footer-col">
              <h4>Product</h4>
              <a href="#pricing">Pricing</a>
              <a href="#api">API</a>
              <a href="#products">Solutions</a>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <Link to="/methodology">Methodology</Link>
              <Link to="/sources">Sources</Link>
              <Link to="/privacy">Privacy</Link>
            </div>
            <div className="footer-col">
              <h4>Contact</h4>
              <a href="mailto:enterprise@framecast.news">enterprise@framecast.news</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Framecast News. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default EnterprisePage;
